import { Tooltip } from 'antd';
import { NUTRITION_OVER_AREA_MESSAGE } from 'constants/messageConstants';
import {
  NUTRITION_ANALYSIS_EMPTY_LABEL,
  NUTRITION_AREA_UNIT_HA,
  NUTRITION_PART_AREA_TOOLTIP,
} from 'constants/uiConstants';
import React from 'react';
import { LuAlertTriangle } from 'react-icons/lu';
import { NUTRIENT_SHORT_LABELS } from '../computeLiveTotals';
import type { NutritionProductLine } from './computeNutritionAudit';
import { formatAnalysis, formatAreaHa } from './nutritionFormat';
import TipContent from './TipContent';

/** Applied to less than the plan's area — compared at the 0.1 ha shown, so rounding doesn't count. */
const isPartArea = (line: NutritionProductLine) =>
  Math.round(line.areaHa * 10) < Math.round(line.planAreaHa * 10);

/** Applied to more than the plan's area — almost always a data-entry slip, and it overstates the nutrients. */
const isOverArea = (line: NutritionProductLine) =>
  Math.round(line.areaHa * 10) > Math.round(line.planAreaHa * 10);

/**
 * A product's name; hover or focus shows its nutrient analysis as recorded
 * on the plan, so the advisor can see why it supplies what it does. The
 * dotted underline says there's more to see. The area it was applied to
 * always shows beside the name; when that's less than the plan's area, a
 * hover explains why its nutrients look smaller than its rate suggests.
 */
const ProductName: React.FC<{ line: NutritionProductLine }> = ({ line }) => {
  const analysis = formatAnalysis(line.analysis, NUTRIENT_SHORT_LABELS);
  const areaText = `${formatAreaHa(line.areaHa)} ${NUTRITION_AREA_UNIT_HA}`;
  return (
    // Name and area stay together, so layouts that push the rate to the
    // right keep them as one unit.
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      <Tooltip title={analysis || NUTRITION_ANALYSIS_EMPTY_LABEL}>
        <span
          tabIndex={0}
          className="cursor-help underline decoration-gray-medium decoration-dotted underline-offset-4 outline-none focus-visible:decoration-charcoal"
        >
          {line.productName}
        </span>
      </Tooltip>
      {isOverArea(line) ? (
        // Flagged, not corrected: the figures follow the plan as entered.
        <Tooltip
          title={
            <TipContent
              tip={NUTRITION_OVER_AREA_MESSAGE(
                formatAreaHa(line.areaHa),
                formatAreaHa(line.planAreaHa)
              )}
            />
          }
        >
          <span
            tabIndex={0}
            className="inline-flex cursor-help items-center gap-1 whitespace-nowrap text-sm font-semibold text-danger-dark outline-none"
          >
            {areaText}
            <LuAlertTriangle size={12} aria-hidden />
          </span>
        </Tooltip>
      ) : isPartArea(line) ? (
        <Tooltip
          title={
            <TipContent
              tip={NUTRITION_PART_AREA_TOOLTIP(
                formatAreaHa(line.areaHa),
                formatAreaHa(line.planAreaHa)
              )}
            />
          }
        >
          <span
            tabIndex={0}
            className="cursor-help whitespace-nowrap text-sm text-gray-dark underline decoration-gray-medium decoration-dotted underline-offset-4 outline-none focus-visible:decoration-charcoal"
          >
            {areaText}
          </span>
        </Tooltip>
      ) : (
        <span className="whitespace-nowrap text-sm text-gray-dark">
          {areaText}
        </span>
      )}
    </span>
  );
};

export default ProductName;
